import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FileSignature, Send, CheckCircle2, XCircle, Trash2,
  FileText, ChevronDown, ChevronUp, Clock, Plus, MessageCircle,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { extractErrorMessage } from '../../utils/errorHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  draft: { label: 'Brouillon', cls: 'border-zinc-500 text-zinc-300' },
  sent: { label: 'Envoyé — en attente', cls: 'border-blue-500/60 text-blue-300' },
  accepted: { label: 'Accepté → Commande', cls: 'border-emerald-500/60 text-emerald-300' },
  refused: { label: 'Refusé — annulé', cls: 'border-red-500/60 text-red-300' },
  expired: { label: 'Expiré — annulé', cls: 'border-orange-500/60 text-orange-300' },
};

const FILTERS = [
  { key: 'all', label: 'Tous' },
  { key: 'draft', label: 'Brouillons' },
  { key: 'sent', label: 'Envoyés' },
  { key: 'accepted', label: 'Acceptés' },
  { key: 'refused', label: 'Refusés' },
  { key: 'expired', label: 'Expirés' },
];

const daysLeft = (validUntil) => {
  if (!validUntil) return null;
  const diff = new Date(validUntil) - new Date();
  return Math.ceil(diff / 86400000);
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return iso; }
};

const AdminQuotes = () => {
  const { isStaff } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // quote id being processed

  useEffect(() => {
    if (!isStaff) navigate('/connexion');
  }, [isStaff, navigate]);

  const fetchQuotes = useCallback(async (f = filter) => {
    try {
      const qs = f !== 'all' ? `?status=${f}` : '';
      const res = await axios.get(`${API_URL}/api/admin/quotes${qs}`);
      setQuotes(res.data.quotes || []);
      setCounts(res.data.counts || {});
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Erreur de chargement'));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  const action = async (id, fn, successMsg, targetStatus) => {
    setBusy(id);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      if (targetStatus && filter !== 'all' && filter !== targetStatus) {
        setFilter(targetStatus); // triggers refetch via useEffect
      } else {
        fetchQuotes();
      }
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Échec de l\'opération'));
    } finally {
      setBusy(null);
    }
  };

  const sendQuote = (id, days) =>
    action(id, () => axios.post(`${API_URL}/api/admin/quotes/${id}/send`, { validity_days: days }),
      'Devis envoyé — stock réservé', 'sent');

  const acceptQuote = (id) =>
    action(id, async () => {
      const res = await axios.post(`${API_URL}/api/admin/quotes/${id}/accept`);
      toast.success('Devis accepté — commande créée', {
        action: { label: 'Voir les commandes', onClick: () => navigate('/admin/commandes') },
      });
      return res;
    }, null, 'accepted');

  const refuseQuote = (id) =>
    action(id, () => axios.post(`${API_URL}/api/admin/quotes/${id}/refuse`),
      'Devis refusé — stock restitué', 'refused');

  const deleteQuote = (id) =>
    action(id, () => axios.delete(`${API_URL}/api/admin/quotes/${id}`), 'Brouillon supprimé');

  const downloadPdf = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/quotes/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Impossible de générer le PDF'));
    }
  };

  const openWhatsApp = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/quotes/${id}/whatsapp`);
      window.open(res.data.whatsapp_url, '_blank');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Numéro de téléphone manquant'));
    }
  };

  if (!isStaff) return null;

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] pt-24 pb-16" data-testid="admin-quotes-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <Link to="/admin" className="text-zinc-500 hover:text-[color:var(--color-primary)] text-xs flex items-center gap-1 mb-2">
              <ArrowLeft className="w-3 h-3" /> Tableau de bord
            </Link>
            <p className="text-[color:var(--color-primary)] text-xs uppercase tracking-[0.3em] mb-2">Devis</p>
            <h1 className="text-3xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Devis clients
            </h1>
            <p className="text-zinc-500 text-sm mt-1">
              Brouillon → Envoi (stock réservé) → Accepté (commande) ou Refusé/Expiré (stock restitué).
            </p>
          </div>
          <Link to="/admin/preparation"
            data-testid="quotes-new-btn"
            className="flex items-center gap-2 bg-[color:var(--color-primary)] text-black px-4 py-2.5 text-xs uppercase tracking-wider hover:bg-[color:var(--color-primary-hover)] transition-colors">
            <Plus className="w-4 h-4" /> Nouveau devis (comptoir)
          </Link>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {FILTERS.map((f) => {
            const count = f.key === 'all'
              ? Object.values(counts).reduce((a, b) => a + b, 0)
              : (counts[f.key] || 0);
            return (
              <button key={f.key} type="button"
                onClick={() => { setFilter(f.key); }}
                data-testid={`quotes-filter-${f.key}`}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${
                  filter === f.key
                    ? 'border-[color:var(--color-primary)] text-[color:var(--color-primary)] bg-[color:rgb(var(--primary-rgb)/0.10)]'
                    : 'border-white/10 text-zinc-400 hover:border-white/30'}`}>
                {f.label} ({count})
              </button>
            );
          })}
        </div>

        {loading ? (
          <p className="text-zinc-500 text-sm py-12 text-center">Chargement…</p>
        ) : quotes.length === 0 ? (
          <div className="border border-white/10 bg-[color:var(--color-surface)] p-10 text-center" data-testid="quotes-empty">
            <FileSignature className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">Aucun devis pour ce filtre.</p>
            <p className="text-zinc-600 text-xs mt-2">
              Créez un devis depuis le comptoir POS avec le bouton « Enregistrer comme devis ».
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {quotes.map((q) => (
              <QuoteCard key={q.id} quote={q} busy={busy === q.id}
                onSend={sendQuote} onAccept={acceptQuote} onRefuse={refuseQuote}
                onDelete={deleteQuote} onPdf={downloadPdf} onWhatsApp={openWhatsApp} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const QuoteCard = ({ quote, busy, onSend, onAccept, onRefuse, onDelete, onPdf, onWhatsApp }) => {
  const [expanded, setExpanded] = useState(false);
  const [validityDays, setValidityDays] = useState(quote.validity_days || 15);
  const meta = STATUS_META[quote.status] || STATUS_META.draft;
  const ref = (quote.id || '').slice(-8).toUpperCase();
  const remaining = quote.status === 'sent' ? daysLeft(quote.valid_until) : null;

  return (
    <div className="border border-white/10 bg-[color:var(--color-surface)]" data-testid={`quote-card-${quote.id}`}>
      <div className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${meta.cls}`}
                data-testid={`quote-status-${quote.id}`}>
            {meta.label}
          </span>
          <p className="text-white text-sm font-medium">Devis #{ref}</p>
          <p className="text-zinc-500 text-xs">{fmtDate(quote.created_at)}</p>
          {remaining !== null && (
            <span className={`flex items-center gap-1 text-[11px] px-2 py-0.5 border ${
              remaining <= 3 ? 'border-orange-500/60 text-orange-300' : 'border-white/10 text-zinc-400'}`}>
              <Clock className="w-3 h-3" />
              {remaining > 0 ? `${remaining} j restants` : 'Expire aujourd\'hui'}
            </span>
          )}
          <span className="text-white text-lg font-light ml-auto" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {Number(quote.total || 0).toFixed(2)} €
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-2">
          <p className="text-zinc-300 text-sm">{quote.customer_name}</p>
          {quote.customer_phone && <p className="text-zinc-500 text-xs">{quote.customer_phone}</p>}
          <button type="button" onClick={() => setExpanded((v) => !v)}
            data-testid={`quote-expand-${quote.id}`}
            className="flex items-center gap-1 text-zinc-500 hover:text-[color:var(--color-primary)] text-xs ml-auto">
            {(quote.items || []).length} article(s)
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>

        {expanded && (
          <div className="mt-3 border border-white/5 divide-y divide-white/5" data-testid={`quote-items-${quote.id}`}>
            {(quote.items || []).map((it, i) => (
              <div key={`${it.product_id}-${i}`} className="flex items-center gap-3 px-3 py-2 text-xs">
                <span className="text-white flex-1 truncate">{it.product_name}</span>
                <span className="text-zinc-500">{it.brand}</span>
                <span className="text-zinc-400">×{it.quantity}</span>
                <span className="text-zinc-400 w-16 text-right">{Number(it.unit_price).toFixed(2)} €</span>
                <span className="text-white w-16 text-right">{Number(it.total).toFixed(2)} €</span>
              </div>
            ))}
            {quote.notes && (
              <p className="px-3 py-2 text-zinc-500 text-xs italic">Note : {quote.notes}</p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-white/5">
          {quote.status === 'draft' && (
            <>
              <div className="flex items-center gap-1.5">
                <label className="text-zinc-500 text-[11px]">Validité :</label>
                <input type="number" min={1} max={90} value={validityDays}
                  onChange={(e) => setValidityDays(Number(e.target.value))}
                  data-testid={`quote-validity-${quote.id}`}
                  className="w-14 bg-black border border-white/10 text-white px-2 py-1 text-xs focus:border-[color:var(--color-primary)] outline-none" />
                <span className="text-zinc-500 text-[11px]">jours</span>
              </div>
              <button type="button" disabled={busy}
                onClick={() => onSend(quote.id, validityDays)}
                data-testid={`quote-send-${quote.id}`}
                className="flex items-center gap-1.5 bg-[color:var(--color-primary)] text-black px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-[color:var(--color-primary-hover)] disabled:opacity-40 transition-colors">
                <Send className="w-3.5 h-3.5" /> Envoyer (réserver le stock)
              </button>
              <button type="button" disabled={busy}
                onClick={() => onDelete(quote.id)}
                data-testid={`quote-delete-${quote.id}`}
                className="flex items-center gap-1.5 border border-red-500/40 text-red-400 px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-red-500/10 disabled:opacity-40 transition-colors">
                <Trash2 className="w-3.5 h-3.5" /> Supprimer
              </button>
            </>
          )}
          {quote.status === 'sent' && (
            <>
              <button type="button" disabled={busy}
                onClick={() => onAccept(quote.id)}
                data-testid={`quote-accept-${quote.id}`}
                className="flex items-center gap-1.5 bg-emerald-500 text-black px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-emerald-400 disabled:opacity-40 transition-colors">
                <CheckCircle2 className="w-3.5 h-3.5" /> Validé → Commande
              </button>
              <button type="button" disabled={busy}
                onClick={() => onRefuse(quote.id)}
                data-testid={`quote-refuse-${quote.id}`}
                className="flex items-center gap-1.5 border border-red-500/40 text-red-400 px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-red-500/10 disabled:opacity-40 transition-colors">
                <XCircle className="w-3.5 h-3.5" /> Refusé
              </button>
              <button type="button" onClick={() => onWhatsApp(quote.id)}
                data-testid={`quote-whatsapp-${quote.id}`}
                className="flex items-center gap-1.5 border border-emerald-500/40 text-emerald-300 px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-emerald-500/10 transition-colors">
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
              </button>
            </>
          )}
          {quote.status === 'accepted' && quote.order_id && (
            <Link to="/admin/commandes"
              data-testid={`quote-order-link-${quote.id}`}
              className="flex items-center gap-1.5 border border-emerald-500/40 text-emerald-300 px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-emerald-500/10 transition-colors">
              <CheckCircle2 className="w-3.5 h-3.5" /> Voir la commande #{quote.order_id.slice(-8).toUpperCase()}
            </Link>
          )}
          <button type="button" onClick={() => onPdf(quote.id)}
            data-testid={`quote-pdf-${quote.id}`}
            className="flex items-center gap-1.5 border border-white/10 text-zinc-300 px-3 py-1.5 text-xs uppercase tracking-wider hover:border-[color:rgb(var(--primary-rgb)/0.50)] hover:text-[color:var(--color-primary)] transition-colors ml-auto">
            <FileText className="w-3.5 h-3.5" /> PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminQuotes;
